/**
 * Effective styles = template styles overlaid with the draft's custom styles.
 *
 * A custom style stored in the draft shadows a template style of the same
 * `category.name`: the draft definition wins and the template entry is replaced.
 * Custom style records are seeded from their base on write, so replacing the whole
 * entry does not lose non-edited props. See docs/custom-text-styles.md.
 */

/**
 * Merge template styles with the draft custom styles, per `[category][name]`.
 * @param {object} template Parabo template data
 * @param {object} [customStyles] Draft custom styles `{ [category]: { [name]: styleSpec } }`
 * @returns {object} Merged styles map in the same shape as `template.template.styles`.
 */
export const getEffectiveStyles = (template, customStyles) => {
  const templateStyles = template?.template?.styles ?? {};
  if (customStyles == null || Object.keys(customStyles).length === 0) {
    return templateStyles;
  }
  const categories = new Set([...Object.keys(templateStyles), ...Object.keys(customStyles)]);
  const merged = {};
  categories.forEach((category) => {
    merged[category] = {
      ...(templateStyles[category] ?? {}),
      ...(customStyles[category] ?? {}),
    };
  });
  return merged;
};

/**
 * Resolve a single style by `styleName`, preferring the draft custom style.
 * Returns `undefined` when neither the draft nor the template defines it, matching
 * the previous `get(template.template.styles, styleName)` contract.
 * @param {object} template Parabo template data
 * @param {object} [customStyles] Draft custom styles
 * @param {string} styleName Dotted `category.name`
 * @returns {object|undefined}
 */
export const getEffectiveStyle = (template, customStyles, styleName) => {
  if (!styleName) {
    return undefined;
  }
  const [category, name] = styleName.split('.');
  if (!category || !name) {
    return undefined;
  }
  const custom = customStyles?.[category]?.[name];
  if (custom != null) {
    return custom;
  }
  return template?.template?.styles?.[category]?.[name];
};
